// 2ECOMP: marker animation — smooth marker movement for all devices
import { useEffect, useRef, useCallback } from 'react';
import { map } from '../core/MapView';

// 2ECOMP: marker animation — constants
const MIN_DISTANCE = 10; // meters — below this, snap (GPS drift)
const MAX_DISTANCE = 5000; // meters — above this, snap (teleport)
const MIN_DURATION = 500; // ms
const MAX_DURATION = 3000; // ms
const DISTANCE_FACTOR = 15; // ms per meter
const FRAME_INTERVAL = 33; // ms (~30fps)

// 2ECOMP: marker animation — approximate distance in meters (Equirectangular)
function approxDistanceMeters(lng1, lat1, lng2, lat2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const x = Math.cos((lat1 + lat2) / 2 * Math.PI / 180) * dLng;
  return R * Math.sqrt(dLat * dLat + x * x);
}

// 2ECOMP: marker animation — interpolate angle via shortest arc
function interpolateCourse(from, to, t) {
  const f = from ?? 0;
  const d = (to ?? 0) - f;
  const diff = d > 180 ? d - 360 : d < -180 ? d + 360 : d;
  return ((f + diff * t) % 360 + 360) % 360;
}

// 2ECOMP: marker animation — cubic ease-out (decelerates at end)
function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

// 2ECOMP: marker animation — recompute iconDegrees & rotation from course
// Replicates the exact algorithm from MapPositions.jsx createFeature()
function computeRotationProps(course, originalProps) {
  const iconDegrees = course - (course % 22.5);
  const rotation = course % 22.5;
  return {
    ...originalProps,
    rotation: Number.isFinite(rotation) ? rotation : 0,
    iconDegrees: Number.isFinite(iconDegrees) ? iconDegrees : 0,
  };
}

// 2ECOMP: marker animation — reconstruct course from feature properties
// createFeature() decomposes course into iconDegrees + rotation
function extractCourse(properties) {
  const deg = properties.iconDegrees ?? 0;
  const rot = properties.rotation ?? 0;
  return deg + rot;
}

/**
 * useDeviceAnimation — smooth marker animation for ALL visible devices.
 *
 * When enabled, this hook takes over setData() calls on both map sources.
 * It detects position changes, creates interpolation animations, and
 * calls setData() directly in a throttled RAF loop (~30fps).
 *
 * When disabled, it does nothing — the existing RAF batch in MapPositions handles updates.
 *
 * @param {Object} params
 * @param {string} params.sourceId - MapLibre source ID for non-selected devices
 * @param {string} params.selectedSourceId - MapLibre source ID for selected device
 * @param {Array} params.mainFeatures - GeoJSON features for non-selected devices
 * @param {Array} params.selectedFeatures - GeoJSON features for selected device (0 or 1)
 * @param {boolean} params.enabled - animateDeviceMove user preference
 * @returns {boolean} Whether the hook is managing setData() (true when enabled)
 */
export default function useDeviceAnimation({
  sourceId, selectedSourceId, mainFeatures, selectedFeatures, enabled,
}) {
  // 2ECOMP: marker animation — refs (never cause re-render)
  const prevPositionsRef = useRef(new Map()); // Map<deviceId, {coords:[lng,lat], course:number}>
  const activeAnimationsRef = useRef(new Map()); // Map<deviceId, AnimState>
  const rafIdRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const isLoopRunningRef = useRef(false);

  // 2ECOMP: marker animation — latest feature arrays for RAF loop access
  const latestMainRef = useRef([]);
  const latestSelectedRef = useRef([]);
  const sourceIdRef = useRef(sourceId);
  const selectedSourceIdRef = useRef(selectedSourceId);

  // Keep source ID refs current
  sourceIdRef.current = sourceId;
  selectedSourceIdRef.current = selectedSourceId;

  // 2ECOMP: marker animation — RAF loop (throttled ~30fps)
  const startLoop = useCallback(() => {
    if (isLoopRunningRef.current) return;
    isLoopRunningRef.current = true;

    function tick(now) {
      // Throttle: skip frame if too close to previous
      if (now - lastFrameTimeRef.current < FRAME_INTERVAL) {
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }
      lastFrameTimeRef.current = now;

      const animations = activeAnimationsRef.current;

      if (animations.size === 0) {
        // No active animations — do a final setData with current features and stop
        map.getSource(sourceIdRef.current)?.setData({
          type: 'FeatureCollection',
          features: latestMainRef.current,
        });
        map.getSource(selectedSourceIdRef.current)?.setData({
          type: 'FeatureCollection',
          features: latestSelectedRef.current,
        });
        isLoopRunningRef.current = false;
        rafIdRef.current = null;
        return;
      }

      // Build interpolated features map
      const interpolated = new Map(); // deviceId → interpolated feature
      const finished = [];

      animations.forEach((anim, deviceId) => {
        const progress = Math.min((now - anim.startTime) / anim.duration, 1);
        const eased = easeOutCubic(progress);

        const lng = anim.fromCoords[0] + (anim.toCoords[0] - anim.fromCoords[0]) * eased;
        const lat = anim.fromCoords[1] + (anim.toCoords[1] - anim.fromCoords[1]) * eased;
        const course = interpolateCourse(anim.fromCourse, anim.toCourse, eased);

        interpolated.set(deviceId, {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: computeRotationProps(course, anim.feature.properties),
        });

        if (progress >= 1) {
          finished.push(deviceId);
        }
      });

      // Remove finished animations
      for (let i = 0; i < finished.length; i++) {
        animations.delete(finished[i]);
      }

      // Build FeatureCollections — replace animated features with interpolated versions
      const mainResult = latestMainRef.current.map((f) => (
        interpolated.get(f.properties.deviceId) || f
      ));
      const selectedResult = latestSelectedRef.current.map((f) => (
        interpolated.get(f.properties.deviceId) || f
      ));

      // setData() directly on MapLibre sources
      map.getSource(sourceIdRef.current)?.setData({
        type: 'FeatureCollection',
        features: mainResult,
      });
      map.getSource(selectedSourceIdRef.current)?.setData({
        type: 'FeatureCollection',
        features: selectedResult,
      });

      // Continue loop if there are still active animations
      if (animations.size > 0) {
        rafIdRef.current = requestAnimationFrame(tick);
      } else {
        isLoopRunningRef.current = false;
        rafIdRef.current = null;
      }
    }

    rafIdRef.current = requestAnimationFrame(tick);
  }, []);

  // 2ECOMP: marker animation — detect feature changes, create/cancel animations
  useEffect(() => {
    if (!enabled) {
      // When disabled, clear all animation state
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      isLoopRunningRef.current = false;
      activeAnimationsRef.current.clear();
      // Don't clear prevPositionsRef — keep it warm for re-enable
      return;
    }

    const main = mainFeatures || [];
    const sel = selectedFeatures || [];

    // Update latest refs for RAF loop
    latestMainRef.current = main;
    latestSelectedRef.current = sel;

    // Process all features to detect position changes
    const allFeatures = main.length > 0 && sel.length > 0
      ? [...main, ...sel]
      : main.length > 0 ? main : sel;
    const currentIds = new Set();
    for (let i = 0; i < allFeatures.length; i++) {
      const feature = allFeatures[i];
      const deviceId = feature.properties.deviceId;
      const [newLng, newLat] = feature.geometry.coordinates;
      const newCourse = extractCourse(feature.properties);
      currentIds.add(deviceId);

      const prev = prevPositionsRef.current.get(deviceId);

      if (!prev) {
        // First position for this device — save, no animation
        prevPositionsRef.current.set(deviceId, {
          coords: [newLng, newLat],
          course: newCourse,
        });
        continue;
      }

      // Check if position actually changed
      if (prev.coords[0] === newLng && prev.coords[1] === newLat) {
        continue;
      }

      const dist = approxDistanceMeters(prev.coords[0], prev.coords[1], newLng, newLat);

      if (dist < MIN_DISTANCE || dist > MAX_DISTANCE) {
        // Snap — update prev, remove any active animation, no animation
        prevPositionsRef.current.set(deviceId, {
          coords: [newLng, newLat],
          course: newCourse,
        });
        activeAnimationsRef.current.delete(deviceId);
        continue;
      }

      // ANIMATE this device
      let fromCoords = prev.coords;
      let fromCourse = prev.course;

      // If there's an active animation, start from current interpolated position
      const existing = activeAnimationsRef.current.get(deviceId);
      if (existing) {
        const elapsed = performance.now() - existing.startTime;
        const progress = Math.min(elapsed / existing.duration, 1);
        const eased = easeOutCubic(progress);
        fromCoords = [
          existing.fromCoords[0] + (existing.toCoords[0] - existing.fromCoords[0]) * eased,
          existing.fromCoords[1] + (existing.toCoords[1] - existing.fromCoords[1]) * eased,
        ];
        fromCourse = interpolateCourse(existing.fromCourse, existing.toCourse, eased);
      }

      const duration = Math.min(MAX_DURATION, Math.max(MIN_DURATION, dist * DISTANCE_FACTOR));

      activeAnimationsRef.current.set(deviceId, {
        fromCoords,
        toCoords: [newLng, newLat],
        fromCourse,
        toCourse: newCourse,
        startTime: performance.now(),
        duration,
        feature,
      });

      // Update prevRef with final destination
      prevPositionsRef.current.set(deviceId, {
        coords: [newLng, newLat],
        course: newCourse,
      });

    }

    // Remove entries for devices that disappeared
    prevPositionsRef.current.forEach((_, deviceId) => {
      if (!currentIds.has(deviceId)) {
        prevPositionsRef.current.delete(deviceId);
        activeAnimationsRef.current.delete(deviceId);
      }
    });

    // If there are active animations and loop isn't running, start it
    if (activeAnimationsRef.current.size > 0 && !isLoopRunningRef.current) {
      startLoop();
    } else if (activeAnimationsRef.current.size === 0 && !isLoopRunningRef.current && enabled) {
      // No animations but features changed — do immediate setData
      map.getSource(sourceIdRef.current)?.setData({
        type: 'FeatureCollection',
        features: main,
      });
      map.getSource(selectedSourceIdRef.current)?.setData({
        type: 'FeatureCollection',
        features: sel,
      });
    }
  }, [mainFeatures, selectedFeatures, enabled, startLoop]);

  // 2ECOMP: marker animation — cleanup on unmount
  useEffect(() => () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    activeAnimationsRef.current.clear();
    prevPositionsRef.current.clear();
    isLoopRunningRef.current = false;
  }, []);

  // Return whether this hook is managing setData()
  return enabled;
}
